import React, { useRef, useState, useEffect, useCallback } from 'react';

// Ordre officiel des numéros sur une cible de fléchettes
const DARTBOARD_NUMBERS = [
  20, 1, 18, 4, 13,
  6, 10, 15, 2, 17,
  3, 19, 7, 16, 8,
  11, 14, 9, 12, 5
];

// Rayons relatifs des différentes couronnes (valeurs normalisées entre 0 et 1)
const RING_THRESHOLDS = {
  innerBull: 0.04,
  outerBull: 0.09,
  tripleInner: 0.45,
  tripleOuter: 0.50,
  doubleInner: 0.725,
  doubleOuter: 0.775,
};

const Dartboard = ({
  visible,
  canThrow,
  onHit,
  angleOffset = 0,
  imageSrc = '/Dartboard_unlabeled.svg.png'
}) => {
  const imageRef = useRef(null);
  const overlayRef = useRef(null);
  const [hoverInfo, setHoverInfo] = useState(null);
  

  // Convertit un clic souris en informations de zone (numéro, anneau, etc.)
  const getHitInformation = useCallback(
    (clientX, clientY) => {
      const image = imageRef.current;
      if (!image) return null;
      
      const rect = image.getBoundingClientRect();
      
      
      const x = clientX - rect.left;
      const y = clientY - rect.top;
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      const dx = x - centerX;
      const dy = y - centerY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      const radius = Math.min(rect.width, rect.height) / 2;
      const distanceRatio = distance / radius;

      if (distanceRatio > RING_THRESHOLDS.doubleOuter) {
        return null;
      }

      let zone = 'single';
      let basePoints = 0;
      let sliceIndex = null;
      let innerRatio = 0;
      let outerRatio = 0;

      if (distanceRatio <= RING_THRESHOLDS.innerBull) {
        zone = 'bullseye';
        basePoints = 25;
        innerRatio = 0;
        outerRatio = RING_THRESHOLDS.innerBull;
        return { zone, basePoints, sliceIndex, innerRatio, outerRatio };
      }

      if (distanceRatio <= RING_THRESHOLDS.outerBull) {
        zone = 'bull';
        basePoints = 25;
        innerRatio = RING_THRESHOLDS.innerBull;
        outerRatio = RING_THRESHOLDS.outerBull;
        return { zone, basePoints, sliceIndex, innerRatio, outerRatio };
      }

      const angleRad = Math.atan2(dy, dx);
      let angleDeg = (angleRad * 180) / Math.PI;
      angleDeg = (angleDeg + 90) % 360;
      if (angleDeg < 0) angleDeg += 360;

      let normalizedAngle = (angleDeg + 9 + angleOffset) % 360;
      if (normalizedAngle < 0) normalizedAngle += 360;
      sliceIndex = Math.floor(normalizedAngle / 18) % 20;
      basePoints = DARTBOARD_NUMBERS[sliceIndex];

      if (
        distanceRatio >= RING_THRESHOLDS.doubleInner &&
        distanceRatio <= RING_THRESHOLDS.doubleOuter
      ) {
        zone = 'double';
        innerRatio = RING_THRESHOLDS.doubleInner;
        outerRatio = RING_THRESHOLDS.doubleOuter;
      } else if (
        distanceRatio >= RING_THRESHOLDS.tripleInner &&
        distanceRatio <= RING_THRESHOLDS.tripleOuter
      ) {
        zone = 'triple';
        innerRatio = RING_THRESHOLDS.tripleInner;
        outerRatio = RING_THRESHOLDS.tripleOuter;
      } else if (distanceRatio < RING_THRESHOLDS.tripleInner) {
        zone = 'single';
        innerRatio = RING_THRESHOLDS.outerBull;
        outerRatio = RING_THRESHOLDS.tripleInner;
      } else {
        zone = 'single';
        innerRatio = RING_THRESHOLDS.tripleOuter;
        outerRatio = RING_THRESHOLDS.doubleInner;
      }

      return { zone, basePoints, sliceIndex, innerRatio, outerRatio };
    },
    [angleOffset]
  );

  // Dessine sur le canvas translucide la zone actuellement survolée
  const drawHighlight = useCallback(() => {
    const canvas = overlayRef.current;
    const image = imageRef.current;
    if (!canvas || !image) return;
    const ctx = canvas.getContext('2d');
    const rect = image.getBoundingClientRect();
    
    canvas.width = rect.width;
    canvas.height = rect.height;
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (!hoverInfo) return;
// décalage manuel, en pixels, à ajuster
    const offsetX = -5; // ex : +3 ou -3
    const offsetY = -5; // ex : +2 ou -2

    const radius = Math.min(rect.width, rect.height) / 2 + offsetX;
    const centerX = rect.width / 2 + offsetX;
    const centerY = rect.height / 2 + offsetY;
    const colors = {
      single: 'rgba(255, 255, 255, 0.35)',
      double: 'rgba(40, 167, 69, 0.4)',
      triple: 'rgba(220, 53, 69, 0.4)',
      bull: 'rgba(255, 193, 7, 0.45)',
      bullseye: 'rgba(255, 0, 0, 0.5)'
    };

    ctx.fillStyle = colors[hoverInfo.zone] || 'rgba(255,255,255,0.3)';

    if (hoverInfo.zone === 'bull' || hoverInfo.zone === 'bullseye') {
      ctx.beginPath();
      ctx.arc(centerX, centerY, hoverInfo.outerRatio * radius, 0, Math.PI * 2);
      ctx.arc(centerX, centerY, hoverInfo.innerRatio * radius, Math.PI * 2, 0, true);
      ctx.closePath();
      ctx.fill();
      return;
    }

    if (hoverInfo.sliceIndex === null) return;
    const sliceCenter = hoverInfo.sliceIndex * 18;
    const topStart = sliceCenter - 9 - angleOffset;
    const topEnd = sliceCenter + 9 - angleOffset;
    const startRad = ((topStart - 90) * Math.PI) / 180;
    const endRad = ((topEnd - 90) * Math.PI) / 180;

    ctx.beginPath();
    ctx.arc(centerX, centerY, hoverInfo.outerRatio * radius, startRad, endRad, false);
    ctx.arc(centerX, centerY, hoverInfo.innerRatio * radius, endRad, startRad, true);
    ctx.closePath();
    ctx.fill();
  }, [hoverInfo, angleOffset]);

  useEffect(() => {
    if (!visible) {
      setHoverInfo(null);
      const canvas = overlayRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        ctx && ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
      return;
    }
    drawHighlight();
  }, [visible, hoverInfo, drawHighlight]);

  const handleClick = (event) => {
    if (!visible || !canThrow) return;
    const info = getHitInformation(event.clientX, event.clientY);
    if (!info) return;
    onHit(info.basePoints, info.zone);
  };

  const handleMouseMove = (event) => {
    if (!visible) return;
    const info = getHitInformation(event.clientX, event.clientY);
    setHoverInfo(info);
  };

  const handleMouseLeave = () => {
    setHoverInfo(null);
    const canvas = overlayRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx && ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  };

  if (!visible) {
    return null;
  }

  return (
    <div className="dartboard-container">
      <h4>Cible officielle (SVG)</h4>
      <div className="dartboard-real">
        <div className="dartboard-wrapper">
          <img
            src={imageSrc}
            alt="Cible de fléchettes"
            className="dartboard-image"
            ref={imageRef}
            onClick={handleClick}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          />
          <canvas ref={overlayRef} className="dartboard-overlay" />
        </div>
      </div>
      <p className="dartboard-help">
        🎯 Survolez et cliquez sur la cible pour confirmer vos points
      </p>
    </div>
  );
};

export default Dartboard;

